import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent, LazyMotion, domAnimation } from 'framer-motion';
import PrivacyPolicy from './components/PrivacyPolicy';
import './App.css';
import logoGif from './logo.gif';
import bannerGif from './banner.gif';
import TermsOfService from './components/TermsOfService';

const OptimizedMotion = ({ children }) => (
  <LazyMotion features={domAnimation}>
    {children}
  </LazyMotion>
);

function MainApp({ isMobile, isScrolled, activeSection, setActiveSection }) {
  const musicNotes = useMemo(() => {
    if (isMobile) return null;
    return (
      <div className="music-notes">
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={i}
            className="music-note"
            initial={{ y: -100, opacity: 0 }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.8, 1, 0.8],
              x: Math.random() * 100 - 50
            }}
            transition={{
              duration: 3 + Math.random() * 5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            style={{
              left: `${10 + (i * 20)}%`,
              animationDelay: `${i * 0.5}s`
            }}
          >
            {i % 2 === 0 ? '♪' : '♫'}
          </motion.div>
        ))}
      </div>
    );
  }, [isMobile]);

  const features = useMemo(() => [
    { icon: '🎵', title: 'High Quality Audio', desc: '128kbps audio streaming' },
    { icon: '⚡', title: 'Low Latency', desc: 'Minimal delay for seamless experience' },
    { icon: '📊', title: 'Queue System', desc: 'Advanced queue with playlist support' },
    { icon: '🔍', title: 'Multi-Source', desc: 'YouTube, Spotify, SoundCloud, Jiosaavn' },
    { icon: '🤖', title: 'Leaderboard System', desc: 'Ranking system' },
    { icon: '🤖', title: 'Badge System', desc: 'New Badges for every levels' },
    { icon: '🎛️', title: 'Equalizer', desc: 'Customizable audio presets' },
    { icon: '🤖', title: '24/7 Uptime', desc: 'Reliable hosting' },
  ], []);



  const commands = useMemo(() => ({
    Info: [
      { command: '/help', desc: "Shows bot's available commands" },
      { command: '/invite', desc: "Get bot's invite link and support server" },
      { command: '/leaderboard', desc: "Displays the leaderboard for the server" },
      { command: '/rank', desc: "Displays info of user on the server" },
      { command: '/ping', desc: "Displays the bot, API, and database latencies." },
      { command: '/stats', desc: "Displays information about the bot." },
      { command: '/dev', desc: "Displays information about the bot dev." },
    ],
    Music: [
      { command: '/join', desc: 'Joins the voice channel' },
      { command: '/play', desc: 'Plays a song' },
      { command: '/dedicate', desc: 'Dedicate & Plays a song' },
      { command: '/pause', desc: 'Pauses the current song' },
      { command: '/resume', desc: 'Resumes the paused song' },
      { command: '/skip', desc: 'Skips the current song' },
      { command: '/previous', desc: 'Goes to the previous song in the queue' },
      { command: '/queue', desc: 'Displays the current song queue' },
      { command: '/nowplaying', desc: 'Displays the current song playing' },
      { command: '/remove', desc: 'Removes a song from the queue' },
      { command: '/clearqueue', desc: 'Clears the entire song queue' },
      { command: '/shuffle', desc: 'Shuffles the song queue' },
      { command: '/replay', desc: 'Replays the current song' },
      { command: '/stop', desc: 'Stops the current song and clears the queue' },
      { command: '/disconnect', desc: 'Disconnects the bot from the voice channel' },
      { command: '/loop', desc: 'Loops the current song' },
      { command: '/search', desc: 'Searches for a song' },
      { command: '/seek', desc: 'Seeks to a specific time in the song' },
      { command: '/volume', desc: 'Adjusts the bot\'s volume' },

    ],
    Config: [
      { command: '/24/7', desc: 'Premium Command for 24/7' },
      { command: '/engine', desc: 'Set search engine for yourself' },
      { command: '/button', desc: 'Toggle player button on/off' },
      { command: '/channel status', desc: 'Toggle channel status on/off' },
      { command: '/setup', desc: 'Sets channel for bot' },
      { command: '/getsetup', desc: 'Gets setup channel for bot' },
      { command: '/clearsetup', desc: 'Clear setup channel for bot' },
      { command: '/server no prefix', desc: 'Turn on or off server no prefix' },
    ],
    Filters: [
      { command: '/bassboost', desc: 'Apply bass boost filter' },
      { command: '/8d', desc: 'Apply 8D filter' },
      { command: '/chipmunk', desc: 'Apply chipmunk filter' },
      { command: '/karaoke', desc: 'Apply karaoke filter' },
      { command: '/deepvoice', desc: 'Apply deep voice filter' },
      { command: '/nightcore', desc: 'Apply nightcore filter' },
      { command: '/tremolo', desc: 'Apply tremolo filter' },
      { command: '/vaporwave', desc: 'Apply vaporwave filter' },
      { command: '/reset', desc: 'Reset all filters' },
    ],
  }), []);
  
  

  return (
    <div className="App">
      <div className="optimized-background"></div>
      {musicNotes}

      <motion.header
        className={`header ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        <motion.div
          className="logo-container"
          whileHover={{ scale: 1.05 }}
        >
          <motion.img
            src={logoGif}
            alt="Music Bot Logo"
            className="logo-gif"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            loading="lazy"
          />
          <motion.h1
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            Folivana Music
          </motion.h1>
        </motion.div>

        <nav className="navbar">
          {['home', 'features', 'commands', 'invite'].map((item) => (
            <motion.button
              key={item}
              className={activeSection === item ? 'active' : ''}
              onClick={() => {
                setActiveSection(item);
                document.getElementById(item)?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {item.charAt(0).toUpperCase() + item.slice(1)}
            </motion.button>
          ))}
        </nav>
      </motion.header>

      <main>
        <section id="home" className="hero-section">
          {!isMobile && (
            <motion.div
              className="hero-banner"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              style={{ zIndex: 0 }}
            >
              <img
                src={bannerGif}
                alt="Music Bot Banner"
                className="banner-gif"
                loading="lazy"
                style={{ opacity: 0.6 }}
              />
              <div className="banner-overlay"></div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
            style={{ zIndex: 1 }}
          >
            <h1>Premium Music <span>Experience</span> for Discord</h1>
            <p>Crystal clear audio, low latency, and powerful features for your server</p>
            <div className="hero-buttons">
            <motion.a
                href="https://discord.com/oauth2/authorize?client_id=1329113834509566055"
                className="invite-button"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setActiveSection('commands');
                  document.getElementById('commands')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }}
              >
              Invite To Server
              </motion.a>

              <motion.button
                className="commands-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setActiveSection('commands');
                  document.getElementById('commands')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }}
              >
                View Commands
              </motion.button>
              <motion.a
                href="https://discord.gg/Deqym9H9vS"
                className="invite-button"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setActiveSection('commands');
                  document.getElementById('commands')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }}
              >
              Support Server
              </motion.a>
            </div>
          </motion.div>
        </section>

        <motion.section 
  id="features" 
  className="features-section"
  initial={{ opacity: 0 }}
  whileInView={{ opacity: 1 }}
  viewport={{ once: true, margin: "0px 0px -50px 0px" }}
  transition={{ duration: 0.8 }}
>
  <div className="section-header">
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ 
        duration: 0.8,
        ease: [0.16, 0.77, 0.47, 0.97] 
      }}
      className="section-title"
    >
      <span className="highlight">Why Choose Our Bot</span>
    </motion.h2>
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 0.8 }}
      viewport={{ once: true }}
      transition={{ delay: 0.3, duration: 0.6 }}
      className="section-subtitle"
    >
      Premium features for an unparalleled music experience
    </motion.p>
  </div>

  <div className="features-grid">
    {features.map((feature, index) => (
      <motion.div
        key={index}
        className="feature-card"
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: "0px 0px -30px 0px" }}
        transition={{ 
          duration: 0.6, 
          delay: index * 0.07,
          ease: [0.34, 1.56, 0.64, 1] 
        }}
        whileHover={{ y: -5 }}
      >
        <motion.div 
          className="feature-icon-container"
          whileHover={{ rotate: 5, scale: 1.1 }}
        >
          <div className="feature-icon">{feature.icon}</div>
        </motion.div>
        <h3 className="feature-title">{feature.title}</h3>
        <p className="feature-desc">{feature.desc}</p>
        <div className="feature-hover-border"></div>
      </motion.div>
    ))}
  </div>
</motion.section>
<section id="commands" className="commands-section">
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, margin: "0px 0px -100px 0px" }}
    transition={{ duration: 0.6 }}
    className="commands-container"
  >
    <h2>Command List</h2>

    {Object.entries(commands).map(([category, cmds], categoryIndex) => (
      <motion.div
        key={category}
        className="commands-category"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
      >
        <div className="category-header">
          <h3>
            <span className="emoji">
              {category === 'Info' ? 'ℹ️' : 
               category === 'Music' ? '🎵' : 
               category === 'Config' ? '⚙️' : 
               category === 'Filters' ? '🎛️' : '🔧'}
            </span> 
            {category}
          </h3>
          <p className="category-description">
            {category === 'Info' ? 'Bot information commands' : 
             category === 'Music' ? 'Music playback controls' : 
             category === 'Config' ? 'Server configuration' : 
             category === 'Filters' ? 'Audio effect filters' : 'Utility commands'}
          </p>
        </div>
        
        <div className="commands-grid">
          {cmds.map((cmd, i) => (
            <motion.div 
              key={i}
              className="command-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <div className="command-header">
                <span className="command-icon">
                  {cmd.icon || 
                   (category === 'Info' ? 'ℹ️' : 
                    category === 'Music' ? '🎵' : 
                    category === 'Config' ? '⚙️' : 
                    category === 'Filters' ? '🎛️' : '🔧')}
                </span>
                <h4 className="command-name">{cmd.command}</h4>
              </div>
              <p className="command-desc">{cmd.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    ))}
  </motion.div>
</section>


        <section id="invite" className="invite-section">
          <motion.div
            className="invite-container"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "0px 0px -100px 0px" }}
            transition={{ duration: 0.6 }}
          >
            <h2>Ready to Enhance Your Server?</h2>
            <p>Add Folivana Music to your Discord server today!</p>

            <motion.a
              href="https://discord.com/oauth2/authorize?client_id=1329113834509566055"
              className="invite-button"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Invite Now
            </motion.a>

            <div className="server-count">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: 'spring' }}
              >
                <span>71000+</span> Users
              </motion.div>
            </div>
          </motion.div>
        </section>
      </main>

      <motion.footer
        className="footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-logo">
              <motion.img
                src={logoGif}
                alt="Music Bot Logo"
                className="footer-logo-img"
                whileHover={{ scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400 }}
                loading="lazy"
              />
              <h3>Folivana Music</h3>
            </div>

            <div className="footer-legal-links">
              <Link to="/PrivacyPolicy">Privacy Policy</Link>
              <span>•</span>
              <Link to="/tos">Terms of Service</Link>
            </div>


          </div>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} Folivana Music. All rights reserved.</p>
            <div className="footer-credits">
              <span>Made with ❤️ by just-Nixx</span>
            </div>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 50);
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (

    <Router>
      <OptimizedMotion>
        <Routes>
          <Route path="/" element={
            <MainApp
              isMobile={isMobile}
              isScrolled={isScrolled}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
            />
          } />
          <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
          <Route path="/tos" element={<TermsOfService />} />
        </Routes>
      </OptimizedMotion>
    </Router>
  );
}

export default App;

